import { Lock } from "@phosphor-icons/react";
import { TextInput, TextInputProps } from "flowbite-react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import React, { forwardRef, SVGProps, useState } from "react";
import { useTranslation } from "react-i18next";

interface PasswordInputProps extends Omit<TextInputProps, "type" | "icon"> {
  label?: string;
  error?: string;
  animationDirection?: "vertical" | "horizontal";
  leftIcon?: React.FC<SVGProps<SVGSVGElement>>;
  showIcon?: React.FC<SVGProps<SVGSVGElement>>;
  hideIcon?: React.FC<SVGProps<SVGSVGElement>>;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      error,
      animationDirection = "vertical",
      leftIcon = Lock,
      showIcon = Eye,
      hideIcon = EyeSlash,
      autoComplete = "current-password",
      onChange,
      onBlur,
      name,
      rightIcon,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <div>
        {label && (
          <div className="mb-2 block">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
          </div>
        )}
        <div className="relative">
          <TextInput
            ref={ref}
            name={name}
            type={showPassword ? "text" : "password"}
            icon={leftIcon}
            color={error ? "failure" : undefined}
            helperText={error}
            autoComplete={autoComplete}
            aria-label={label || t("auth.password.ariaLabel")}
            onChange={handleChange}
            onBlur={onBlur}
            rightIcon={rightIcon}
            {...props}
          />
          {!rightIcon && (
            <button
              type="button"
              className="absolute right-3 top-[22px] -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all duration-300 transform hover:scale-110"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t("auth.password.hide") : t("auth.password.show")}
              tabIndex={-1}
            >
              <div className="relative h-4 w-4 overflow-hidden">
                {animationDirection === "vertical" ? (
                  <>
                    <div
                      className={`absolute transition-transform duration-300 ease-in-out ${showPassword
                          ? "translate-y-0 opacity-100"
                          : "-translate-y-full opacity-0"
                        }`}
                    >
                      {React.createElement(hideIcon, { className: "h-4 w-4" })}
                    </div>
                    <div
                      className={`absolute transition-transform duration-300 ease-in-out ${showPassword
                          ? "translate-y-full opacity-0"
                          : "translate-y-0 opacity-100"
                        }`}
                    >
                      {React.createElement(showIcon, { className: "h-4 w-4" })}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`absolute transition-transform duration-300 ease-in-out ${showPassword
                          ? "translate-x-0 opacity-100"
                          : "translate-x-full opacity-0"
                        }`}
                    >
                      {React.createElement(hideIcon, { className: "h-4 w-4" })}
                    </div>
                    <div
                      className={`absolute transition-transform duration-300 ease-in-out ${showPassword
                          ? "-translate-x-full opacity-0"
                          : "translate-x-0 opacity-100"
                        }`}
                    >
                      {React.createElement(showIcon, { className: "h-4 w-4" })}
                    </div>
                  </>
                )}
              </div>
            </button>
          )}
        </div>
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
